'use client'

import type { User } from '@supabase/supabase-js'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'

// User with extended profile information
export interface UserWithProfile extends User {
  avatar_url?: string
  full_name?: string
}

// =============================================================================
// QUERY OPTIONS
// =============================================================================

export function currentUserQueryOptions() {
  return queryOptions({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const supabase = createClient()
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser()

      if (error) {
        throw error
      }
      if (!user) {
        return null
      }

      // Extract profile data from user metadata
      const userWithProfile: UserWithProfile = {
        ...user,
        full_name: user.user_metadata?.full_name ?? user.user_metadata?.name,
        avatar_url: user.user_metadata?.avatar_url ?? user.user_metadata?.picture,
      }

      return userWithProfile
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - user data is relatively stable
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Get the current authenticated user.
 * Returns null if not authenticated.
 */
export function useUser() {
  return useQuery(currentUserQueryOptions())
}

/**
 * Get the current user's ID.
 * Returns undefined if not authenticated.
 */
export function useUserId(): string | undefined {
  const { data: user } = useUser()
  return user?.id
}

/**
 * Get the current user's email.
 * Returns null if not authenticated.
 */
export function useUserEmail(): string | null {
  const { data: user } = useUser()
  return user?.email ?? null
}

/**
 * Get the current user's display name.
 * Falls back to email if no name is set.
 */
export function useUserDisplayName(): string | null {
  const { data: user } = useUser()
  if (!user) {
    return null
  }
  return user.full_name ?? user.email ?? null
}

/**
 * Get the current user's avatar URL.
 * Returns null if no avatar is set.
 */
export function useUserAvatar(): string | null {
  const { data: user } = useUser()
  return user?.avatar_url ?? null
}
